import React, { useEffect, useState, useRef } from "react";
import {
  Modal,
  View,
  Text,
  TouchableOpacity,
  StyleSheet,
  ScrollView,
} from "react-native";
import { Ionicons } from "@expo/vector-icons";
import { observer } from "mobx-react";
import { useTranslation } from "react-i18next";

import Colors from "../../Resources/Color";
import Fonts from "../../Resources/Fonts";
import GlobalStyles from "../../Resources/Styles";
import { useDevice, useScreenDimensions } from "../_layout";
import MultipleSegmentedPicker from "../Components/MultipleSegmentedPicker";
import { useToast } from "../contexts/ToastContext";
import { BlockPlanificationConstants } from "../../Resources/Constants";
import { useModalKeyboardShortcuts } from "../platform/guards/modalKeyboardShortcuts";

const LinkSlotView = observer(({ isVisible, onClose, exercise }) => {
  const { t, i18n } = useTranslation();
  const { screenSize } = useScreenDimensions();
  const { isMobile } = useDevice();
  const { showToast } = useToast();

  const [closeHovered, setCloseHovered] = useState(false);
  const [saveHovered, setSaveHovered] = useState(false);
  const [cancelHovered, setCancelHovered] = useState(false);

  const block = exercise?.workout?.block;
  const week = exercise?.workout?.week;

  useEffect(() => {
    if (block) {
      block.setProperty("selectedLift", null);
      block.setProperty("selectedPriority", null);
      block.setProperty("selectedSecondary", false);
    }
  }, [block]);

  const getSlotName = () => {
    const { selectedLift: lift, selectedPriority: priority } = block;
    if (!lift || !priority) return null;

    // strip off trailing '-string'
    const priorityBase = priority.replace(/-string$/, "");
    const liftBase = lift.replace(/-string$/, "");
    // capitalize first letter of lift
    const liftCapitalized =
      liftBase.charAt(0).toUpperCase() + liftBase.slice(1);

    return `${priorityBase}${liftCapitalized}-string`;
  };

  const slotName = getSlotName();
  const weekSlots =
    block?.protocolsAndExercises.filter((ex) => ex.getWeek === week) || [];

  const getWorkoutLabel = (targetWorkout) => {
    if (!targetWorkout || !block?.workouts) return "";
    const customWeekName = block?.getWeekName?.(week) || "";
    const weekLabel =
      customWeekName.trim() !== ""
        ? customWeekName
        : t("weekWithNumber-string", { week });
    const sameWeekWorkouts = block.workouts
      .filter((w) => w.week === week)
      .sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
    const workoutIndex = sameWeekWorkouts.findIndex(
      (w) => w.id === targetWorkout.id,
    );
    const workoutNumber =
      workoutIndex > -1 ? workoutIndex + 1 : (targetWorkout?.order ?? 0) + 1;
    const workoutName =
      typeof targetWorkout?.name === "string" ? targetWorkout.name.trim() : "";
    const workoutLabel =
      workoutName ||
      t("workoutWithNumber-string", {
        number: workoutNumber,
      });
    return `${weekLabel} · ${workoutLabel}`;
  };

  let foundFirstSlot = null;
  if (slotName) {
    // Prefer an existing exercise in this workout
    foundFirstSlot =
      weekSlots.find(
        (ex) =>
          ex.protocolSlot === slotName && !ex.isSecondaryProtocol && ex.workout,
      ) ||
      weekSlots.find(
        (ex) => ex.protocolSlot === slotName && !ex.isSecondaryProtocol,
      );
  }
  // Determine if the first slot came from an exercise in this workout
  const firstFromThisWorkout = Boolean(
    foundFirstSlot && foundFirstSlot.workout,
  );

  let foundSecondSlot = null;
  if (slotName) {
    if (firstFromThisWorkout) {
      // Only pick a secondary exercise from the same workout
      foundSecondSlot =
        weekSlots.find(
          (ex) =>
            ex.protocolSlot === slotName &&
            ex.isSecondaryProtocol &&
            ex.workout,
        ) ||
        weekSlots.find(
          (ex) => ex.protocolSlot === slotName && ex.isSecondaryProtocol,
        );
    } else {
      // Fallback: prefer same-workout, but allow any secondary
      foundSecondSlot =
        weekSlots.find(
          (ex) =>
            ex.protocolSlot === slotName &&
            ex.isSecondaryProtocol &&
            ex.workout,
        ) ||
        weekSlots.find(
          (ex) => ex.protocolSlot === slotName && ex.isSecondaryProtocol,
        );
    }
  }

  const disabled =
    ((!block?.selectedLift || !block?.selectedPriority) && !foundSecondSlot) ||
    (foundFirstSlot && !block?.selectedSecondary);

  const save = () => {
    if (disabled) return;

    exercise.setProperty("protocolSlot", slotName);
    if (block.selectedSecondary) {
      exercise.setProperty("isSecondaryProtocol", true);
    } else {
      exercise.setProperty("isSecondaryProtocol", false);
    }

    exercise.workout?.setProperty("hasChanged", true);
    if (block.client) {
      block.client.updateClientTransactionData(block.id);
      block.client.startOrResetBatchTimer();
    } else if (block.coach) {
      block.coach.updateCoachTransactionBlocks(block.id);
      block.coach.startOrResetBatchTimer();
    }

    const slotLabel = slotName ? t(slotName) : "";
    const workoutLabel = getWorkoutLabel(exercise?.workout);
    const exerciseName =
      exercise?.getName?.(t, i18n.language) ||
      exercise?.name ||
      t("exercise-string");

    showToast("exerciseLinkedToastTitle-string", {
      type: "info",
      title: "exerciseLinkedToastTitle-string",
      description: "exerciseLinkedToastDescription-string",
      duration: BlockPlanificationConstants.TOAST_DURATION_MS,
      variables: {
        exerciseName,
        slotName: slotLabel,
        workoutLabel,
      },
    });

    onClose();
  };

  const exerciseName =
    exercise?.getName?.(t, i18n.language) ||
    exercise?.name ||
    t("exercise-string");

  const weekName = block?.getWeekName?.(week)
    ? block.getWeekName(week)
    : t("weekWithNumber-string", { week: week });

  const liftOptions = ["squat-string", "benchPress-string", "deadlift-string"];
  const priorityOptions = [
    "primary-string",
    "secondary-string",
    "tertiary-string",
    "quaternary-string",
    "quinary-string",
  ];

  useModalKeyboardShortcuts({
    isVisible,
    onConfirm: save,
    onCancel: onClose,
    isConfirmEnabled: !disabled,
  });

  return (
    <Modal
      animationType="fade"
      transparent={true}
      visible={isVisible}
      onRequestClose={onClose}
    >
      <View style={GlobalStyles.centeredView}>
        <TouchableOpacity
          onPress={onClose}
          style={GlobalStyles.touchableBackground}
        />
        <View style={styles.modalView}>
          <TouchableOpacity
            style={styles.closeButton}
            onPress={onClose}
            onMouseEnter={() => !isMobile && setCloseHovered(true)}
            onMouseLeave={() => setCloseHovered(false)}
          >
            <Ionicons
              name="close"
              size={22}
              color={closeHovered ? Colors.blackColor : Colors.grayColor}
            />
          </TouchableOpacity>

          <View style={{ gap: 8, marginBottom: 16 }}>
            <Text style={styles.title}>{t("linkExerciseToSlot-string")}</Text>
            <Text style={styles.subtitle}>
              {exerciseName} · {weekName}
            </Text>
          </View>

          <ScrollView
            style={styles.scrollView}
            contentContainerStyle={styles.scrollContent}
            showsVerticalScrollIndicator={false}
          >
            <Text style={styles.sectionLabel}>{t("lift-string")}</Text>
            <ScrollView
              horizontal
              showsHorizontalScrollIndicator={false}
              style={{ marginHorizontal: -24 }}
              contentContainerStyle={{ paddingHorizontal: 24 }}
            >
              <MultipleSegmentedPicker
                options={liftOptions}
                value={liftOptions.indexOf(block?.selectedLift)}
                onChange={(index) =>
                  block?.setProperty("selectedLift", liftOptions[index])
                }
              />
            </ScrollView>

            <Text style={styles.sectionLabel}>{t("priority-string")}</Text>
            <ScrollView
              horizontal
              showsHorizontalScrollIndicator={false}
              style={{ marginHorizontal: -24 }}
              contentContainerStyle={{ paddingHorizontal: 24 }}
            >
              <MultipleSegmentedPicker
                options={priorityOptions}
                value={priorityOptions.indexOf(block?.selectedPriority)}
                onChange={(index) =>
                  block?.setProperty("selectedPriority", priorityOptions[index])
                }
              />
            </ScrollView>

            {foundFirstSlot && !foundSecondSlot && (
              <View
                style={
                  foundFirstSlot?.workout?.id === exercise?.workout?.id
                    ? styles.infoBanner
                    : styles.warningBanner
                }
              >
                <View
                  style={
                    foundFirstSlot?.workout?.id === exercise?.workout?.id
                      ? styles.infoBannerIcon
                      : styles.warningBannerIcon
                  }
                >
                  <Ionicons
                    name={
                      foundFirstSlot?.workout?.id === exercise?.workout?.id
                        ? "help-circle-outline"
                        : "alert-circle-outline"
                    }
                    size={14}
                    color={
                      foundFirstSlot?.workout?.id === exercise?.workout?.id
                        ? Colors.darkgrayColor
                        : Colors.warningTextColor
                    }
                  />
                </View>
                <View
                  style={
                    foundFirstSlot?.workout?.id === exercise?.workout?.id
                      ? styles.infoBannerContent
                      : styles.warningBannerContent
                  }
                >
                  {foundFirstSlot?.workout?.id === exercise?.workout?.id ? (
                    <View style={styles.warningContent}>
                      <View style={{ flex: 1 }}>
                        <Text style={styles.infoBannerText}>
                          {t("alreadyOneExerciseForSlot-string", {
                            exerciseName: foundFirstSlot.getName(
                              t,
                              i18n.language,
                            ),
                          })}
                        </Text>
                      </View>
                      <View style={styles.secondaryPickerContainer}>
                        <MultipleSegmentedPicker
                          options={["No", "yes-string"]}
                          value={block?.selectedSecondary ? 1 : 0}
                          onChange={(index) =>
                            block?.setProperty(
                              "selectedSecondary",
                              index === 0 ? false : true,
                            )
                          }
                          small={true}
                        />
                      </View>
                    </View>
                  ) : (
                    <Text style={styles.warningText}>
                      {t("alreadyOneExerciseForSlotAnotherWorkout-string", {
                        exerciseName: foundFirstSlot.getName(t, i18n.language),
                      })}
                    </Text>
                  )}
                </View>
              </View>
            )}

            {foundFirstSlot && foundSecondSlot && (
              <View style={styles.warningBanner}>
                <View style={styles.warningBannerIcon}>
                  <Ionicons
                    name="alert-circle-outline"
                    size={14}
                    color={Colors.warningTextColor}
                  />
                </View>
                <View style={styles.warningBannerContent}>
                  <Text style={styles.warningText}>
                    {t("ThisSlotIsFull-string", {
                      firstExerciseName: foundFirstSlot.getName(
                        t,
                        i18n.language,
                      ),
                      secondExerciseName: foundSecondSlot.getName(
                        t,
                        i18n.language,
                      ),
                    })}
                  </Text>
                </View>
              </View>
            )}

            {!foundFirstSlot && !foundSecondSlot && (
              <View style={styles.infoBanner}>
                <View style={styles.infoBannerIcon}>
                  <Ionicons
                    name="information-circle-outline"
                    size={14}
                    color={Colors.darkgrayColor}
                  />
                </View>
                <View style={styles.infoBannerContent}>
                  <Text style={styles.infoBannerText}>
                    {t("linkSlotInfo-string")}
                  </Text>
                </View>
              </View>
            )}
          </ScrollView>

          <View style={styles.actionsRow}>
            <TouchableOpacity
              style={[
                styles.secondaryButton,
                cancelHovered && styles.secondaryButtonHovered,
              ]}
              onPress={onClose}
              onMouseEnter={() => !isMobile && setCancelHovered(true)}
              onMouseLeave={() => setCancelHovered(false)}
            >
              <Text style={styles.secondaryText}>{t("cancel-string")}</Text>
            </TouchableOpacity>

            <TouchableOpacity
              style={[
                styles.primaryButton,
                disabled && styles.primaryButtonDisabled,
                !disabled && saveHovered && styles.primaryButtonHovered,
              ]}
              onPress={save}
              disabled={disabled}
              onMouseEnter={() => !isMobile && setSaveHovered(true)}
              onMouseLeave={() => setSaveHovered(false)}
            >
              <Text style={styles.primaryText}>{t("save-string")}</Text>
            </TouchableOpacity>
          </View>
        </View>
      </View>
    </Modal>
  );
});

const styles = StyleSheet.create({
  modalView: {
    backgroundColor: Colors.whiteColor,
    borderRadius: 18,
    padding: 24,
    shadowColor: "#000",
    shadowOffset: {
      width: 0,
      height: 4,
    },
    shadowOpacity: 0.3,
    shadowRadius: 4.65,
    elevation: 8,
    width: 600,
    maxWidth: "92%",
  },
  closeButton: {
    position: "absolute",
    top: 16,
    right: 16,
    width: 32,
    height: 32,
    borderRadius: 16,
    justifyContent: "center",
    alignItems: "center",
    zIndex: 10,
    cursor: "pointer",
  },
  title: {
    ...Fonts.bold18,
    color: Colors.blackColor,
  },
  subtitle: {
    fontSize: 14,
    fontWeight: "500",
    color: Colors.bubbleColor,
  },
  scrollView: {
    maxHeight: 400,
  },
  scrollContent: {
    paddingBottom: 8,
  },
  sectionLabel: {
    fontSize: 12,
    fontWeight: "500",
    color: Colors.grayColor,
    marginTop: 16,
    marginBottom: 8,
    textTransform: "uppercase",
    letterSpacing: 0.5,
  },
  warningBanner: {
    marginTop: 24,
    paddingHorizontal: 12,
    paddingVertical: 10,
    borderRadius: 8,
    backgroundColor: Colors.warningBackgroundColor,
    flexDirection: "row",
    alignItems: "flex-start",
    gap: 8,
  },
  warningBannerIcon: {
    paddingTop: 1,
  },
  warningBannerContent: {
    flex: 1,
    gap: 0,
  },
  warningContent: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 12,
  },
  warningText: {
    fontSize: 11,
    lineHeight: 15,
    color: Colors.warningTextColor,
    flexShrink: 1,
  },
  infoBanner: {
    marginTop: 24,
    paddingHorizontal: 12,
    paddingVertical: 10,
    borderRadius: 8,
    backgroundColor: Colors.lightGrayColor,
    flexDirection: "row",
    alignItems: "flex-start",
    gap: 8,
  },
  infoBannerIcon: {
    paddingTop: 1,
  },
  infoBannerContent: {
    flex: 1,
    gap: 0,
  },
  infoBannerText: {
    fontSize: 11,
    lineHeight: 15,
    color: Colors.mediumGray,
  },
  secondaryPickerContainer: {
    flexShrink: 0,
  },
  actionsRow: {
    flexDirection: "row",
    justifyContent: "flex-end",
    gap: 12,
    marginTop: 24,
  },
  secondaryButton: {
    height: 36,
    paddingHorizontal: 16,
    borderRadius: 12,
    borderWidth: 1,
    borderColor: Colors.borderColor,
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: Colors.whiteColor,
  },
  secondaryButtonHovered: {
    backgroundColor: Colors.backgroundColor,
  },
  secondaryText: {
    fontSize: 14,
    fontWeight: "500",
    color: Colors.darkgrayColor,
  },
  primaryButton: {
    height: 36,
    paddingHorizontal: 18,
    borderRadius: 12,
    borderWidth: 1,
    borderColor: Colors.bubbleColor,
    backgroundColor: Colors.bubbleColor,
    alignItems: "center",
    justifyContent: "center",
  },
  primaryButtonDisabled: {
    opacity: 0.6,
  },
  primaryButtonHovered: {
    opacity: 0.9,
  },
  primaryText: {
    fontSize: 14,
    fontWeight: "600",
    color: Colors.whiteColor,
  },
});

export default LinkSlotView;
